/**
 * Pinned ArcGIS / regional layer catalog for on-demand map fetch.
 *
 * URLs are pinned (R1 drift policy) — endpoint migrations require a
 * deliberate PR + review. Hosts must already appear in `server.ts`
 * `ALLOWED_PROXY_HOSTS`.
 */
import type { SiteLayerCategory } from '../types/site';
import type { RefdataDatasetId } from '../types/refdata';

export type ArcgisCatalogGeometryHint = 'point' | 'line' | 'polygon';

export type ArcgisCatalogSourceKind = 'arcgis' | 'peeringdb';

export interface ArcgisCatalogEntry {
  id: string;
  label: string;
  category: SiteLayerCategory;
  geometryHint: ArcgisCatalogGeometryHint;
  /** ArcGIS FeatureServer/MapServer query base URL (no query params). */
  kind: ArcgisCatalogSourceKind;
  queryUrl?: string;
  outFields?: string;
  nameField?: string;
  /** Join non-empty attribute values for line/polygon features with shared TYPE labels. */
  nameFields?: string[];
  descriptionFields?: string[];
  /**
   * Source attributes to preserve verbatim on `SiteLayer.attributes`
   * (additive; see the `attributes` field on `SiteLayer`). Used by the
   * gas-infra layers so numeric capacity attributes (CERT_HP, MAXCAP)
   * survive the Esri→SiteLayer conversion and can feed proximity
   * aggregates in `compileOnSiteInfrastructure`.
   */
  attributeFields?: string[];
  defaultEnabled?: boolean;
  /**
   * RD-1 (E) — when set, fetchRegionalLayers serves this entry from the
   * nationwide refdata snapshot FIRST (live ArcGIS is the fallback; entries
   * without a queryUrl are refdata-only and degrade to a warning when the
   * dataset is unseeded).
   */
  refdataDataset?: RefdataDatasetId;
}

/**
 * HIFLD + EIA layers migrated to the ORNL OpenEnergyDataPortal host on
 * services5 (2026-05). Legacy services1/services.arcgis.com paths under
 * Hp6G80Pky0om7QvQ now return ArcGIS `{error: Invalid URL}`.
 */
const HIFLD_SVC =
  'https://services5.arcgis.com/HDRa0B57OVrv2E1q/ArcGIS/rest/services';

export const ARCGIS_LAYER_CATALOG: readonly ArcgisCatalogEntry[] = [
  {
    id: 'hifld-substations',
    refdataDataset: 'hifld-substations',
    label: 'Electric substations (HIFLD)',
    category: 'infrastructure.electrical',
    geometryHint: 'point',
    kind: 'arcgis',
    queryUrl: `${HIFLD_SVC}/Electric_Substations/FeatureServer/0/query`,
    outFields: 'NAME,MAX_VOLT,MIN_VOLT,TYPE,STATUS,CITY,STATE',
    nameField: 'NAME',
    descriptionFields: ['MAX_VOLT', 'MIN_VOLT', 'TYPE', 'CITY', 'STATE'],
    defaultEnabled: true,
  },
  {
    id: 'hifld-transmission',
    refdataDataset: 'hifld-transmission',
    label: 'Transmission lines (HIFLD)',
    category: 'infrastructure.electrical',
    geometryHint: 'line',
    kind: 'arcgis',
    queryUrl: `${HIFLD_SVC}/Electric_Power_Transmission_Lines/FeatureServer/0/query`,
    outFields: 'TYPE,VOLTAGE,STATUS,OWNER,ID',
    // VOLTAGE + OWNER, not TYPE + VOLTAGE: TYPE is near-constant
    // ("AC; OVERHEAD") across a fetch, so hundreds of segments shared one
    // label and the panel read as a wall of identical truncated rows.
    // "115 kV · PacifiCorp" is scannable and groups naturally.
    nameFields: ['VOLTAGE', 'OWNER'],
    descriptionFields: ['TYPE', 'STATUS', 'ID'],
    defaultEnabled: true,
  },
  {
    id: 'hifld-gas-pipelines',
    refdataDataset: 'gas-pipelines',
    label: 'Natural gas pipelines (HIFLD)',
    category: 'infrastructure.pipeline',
    geometryHint: 'line',
    kind: 'arcgis',
    queryUrl: `${HIFLD_SVC}/Natural_Gas_Pipelines/FeatureServer/0/query`,
    outFields: 'TYPEPIPE,Operator',
    // Operator leads: TYPEPIPE alone is "Interstate"/"Intrastate", which
    // named every pipeline in a fetch identically.
    nameFields: ['Operator', 'TYPEPIPE'],
    descriptionFields: ['TYPEPIPE', 'Operator'],
    defaultEnabled: false,
  },
  // ── Gas-infrastructure proximity Tier 1 (2026-06) ──────────────────
  // EIA/HIFLD point layers on the same ORNL host as the pipelines
  // layer (already allow-listed; live-probed 2026-06). All three carry
  // dataLastEditDate 2021-02-25 — downstream narration MUST stamp the
  // '2021-02' vintage (see `GasInfraProximity.dataVintage`). Sentinel
  // values (-999 / 9999999) are guarded at the compile boundary.
  {
    id: 'hifld-gas-compressors',
    refdataDataset: 'gas-compressors',
    label: 'Natural gas compressor stations (EIA/HIFLD)',
    category: 'infrastructure.pipeline',
    geometryHint: 'point',
    kind: 'arcgis',
    queryUrl: `${HIFLD_SVC}/Natural_Gas_Compressor_Stations/FeatureServer/0/query`,
    outFields: 'NAME,OPERATOR,NUM_UNITS,CERT_HP,STATUS,STATE',
    nameField: 'NAME',
    descriptionFields: ['OPERATOR', 'NUM_UNITS', 'CERT_HP', 'STATUS', 'STATE'],
    attributeFields: ['OPERATOR', 'NUM_UNITS', 'CERT_HP'],
    defaultEnabled: false,
  },
  {
    id: 'hifld-gas-interconnects',
    refdataDataset: 'gas-interconnects',
    label: 'Oil & natural gas pipeline interconnects (EIA/HIFLD)',
    category: 'infrastructure.pipeline',
    geometryHint: 'point',
    kind: 'arcgis',
    // Live schema: connecting-pipeline names are CONNNAME / CONNNAME2 /
    // CONNNAME3 (NOT CONNNAME1 — verified against the FeatureServer).
    queryUrl: `${HIFLD_SVC}/Oil_and_Natural_Gas_Interconnects/FeatureServer/0/query`,
    outFields: 'NAME,COMPNAME,CONNNAME,CONNNAME2,CONNNAME3,STATE',
    nameField: 'COMPNAME',
    descriptionFields: ['CONNNAME', 'CONNNAME2', 'CONNNAME3', 'STATE'],
    attributeFields: ['CONNNAME', 'CONNNAME2', 'CONNNAME3'],
    defaultEnabled: false,
  },
  {
    id: 'hifld-gas-receipt-delivery',
    refdataDataset: 'gas-receipt-delivery',
    label: 'Natural gas receipt/delivery points (EIA/HIFLD)',
    category: 'infrastructure.pipeline',
    geometryHint: 'point',
    kind: 'arcgis',
    // MAXCAP/SCHEDCAP/AVAILCAP native units are Mcf/d (layer avg ≈ 74k
    // Mcf/d ≈ 74 MMcf/d per point — physically sensible; MMcf/d native
    // would exceed national throughput). Converted at the compile
    // boundary via MCF_PER_MMCF.
    queryUrl: `${HIFLD_SVC}/Natural_Gas_Receipt_Delivery_Points/FeatureServer/0/query`,
    outFields: 'NAME,COMPNAME,MAXCAP,SCHEDCAP,AVAILCAP,ZONENAME,STATE',
    nameField: 'NAME',
    descriptionFields: ['COMPNAME', 'MAXCAP', 'AVAILCAP', 'ZONENAME', 'STATE'],
    attributeFields: ['MAXCAP', 'SCHEDCAP', 'AVAILCAP', 'ZONENAME'],
    defaultEnabled: false,
  },
  {
    // The `tiger-states` snapshot's ONLY consumer, and the reason it is
    // seeded at all. State IDENTITY for a site already comes from
    // `tiger-counties` via fipsLookup (county GEOID's first two digits ->
    // STATE_FIPS_TO_POSTAL), so this entry is deliberately a map overlay and
    // nothing more — wiring it into identity too would create a second
    // source of truth for one fact.
    id: 'tiger-state-boundaries',
    refdataDataset: 'tiger-states',
    label: 'State & territory boundaries (Census TIGER)',
    category: 'other',
    geometryHint: 'polygon',
    kind: 'arcgis',
    queryUrl: 'https://tigerweb.geo.census.gov/arcgis/rest/services/TIGERweb/State_County/MapServer/0/query',
    outFields: 'GEOID,STATE,NAME,BASENAME,STUSAB',
    nameField: 'NAME',
    descriptionFields: ['STUSAB', 'GEOID'],
    defaultEnabled: false,
  },
  {
    id: 'eia-service-territories',
    label: 'Electric retail service territories (EIA)',
    category: 'infrastructure.electrical',
    geometryHint: 'polygon',
    kind: 'arcgis',
    queryUrl: `${HIFLD_SVC}/Electric_Retail_Service_Territories/FeatureServer/0/query`,
    outFields: 'NAME,STATE,HOLDING_CO,TYPE',
    nameField: 'NAME',
    descriptionFields: ['HOLDING_CO', 'STATE', 'TYPE'],
    defaultEnabled: false,
  },
  {
    id: 'usfws-critical-habitat',
    label: 'USFWS proposed critical habitat',
    category: 'constraint.environmental',
    geometryHint: 'polygon',
    kind: 'arcgis',
    queryUrl:
      'https://services.arcgis.com/QVENGdaPbd4LUkLV/ArcGIS/rest/services/USFWS_Critical_Habitat/FeatureServer/2/query',
    outFields: 'comname,sciname,status',
    nameField: 'comname',
    descriptionFields: ['sciname', 'status'],
    defaultEnabled: false,
  },
  {
    id: 'usfws-final-critical-habitat',
    label: 'USFWS final critical habitat',
    category: 'constraint.environmental',
    geometryHint: 'polygon',
    kind: 'arcgis',
    queryUrl:
      'https://services.arcgis.com/QVENGdaPbd4LUkLV/ArcGIS/rest/services/USFWS_Critical_Habitat/FeatureServer/0/query',
    outFields: 'comname,sciname,status',
    nameField: 'comname',
    descriptionFields: ['sciname', 'status'],
    defaultEnabled: false,
  },
  {
    id: 'peeringdb-facilities',
    label: 'Colocation / data center facilities (PeeringDB)',
    category: 'infrastructure.fiber',
    geometryHint: 'point',
    kind: 'peeringdb',
    defaultEnabled: true,
  },
  // ── RD-1 (E) — refdata-only overlays (no live ArcGIS fallback; served
  // exclusively from the nationwide snapshots; unseeded → warning). ──
  {
    id: 'nwi-wetlands',
    label: 'Wetlands (NWI)',
    category: 'constraint.environmental',
    geometryHint: 'polygon',
    kind: 'arcgis',
    refdataDataset: 'nwi-wetlands',
    nameField: 'WETLAND_TYPE',
    descriptionFields: ['ACRES', 'ATTRIBUTE'],
    defaultEnabled: false,
  },
  {
    id: 'fema-flood-zones',
    label: 'Flood zones (FEMA NFHL)',
    category: 'constraint.environmental',
    geometryHint: 'polygon',
    kind: 'arcgis',
    refdataDataset: 'fema-nfhl',
    nameField: 'FLD_ZONE',
    descriptionFields: ['ZONE_SUBTY', 'SFHA_TF'],
    defaultEnabled: false,
  },
  {
    id: 'padus-protected-areas',
    label: 'Protected areas (PAD-US)',
    category: 'constraint.environmental',
    geometryHint: 'polygon',
    kind: 'arcgis',
    refdataDataset: 'padus',
    nameField: 'NAME',
    descriptionFields: ['MANAGER', 'DESIGNATION', 'GAP_STATUS'],
    defaultEnabled: false,
  },
  {
    id: 'aiannh-tribal-areas',
    label: 'Tribal lands (AIANNH)',
    category: 'other',
    geometryHint: 'polygon',
    kind: 'arcgis',
    refdataDataset: 'aiannh',
    nameField: 'NAME',
    descriptionFields: ['LSAD', 'GEOID'],
    defaultEnabled: false,
  },
  {
    id: 'wbd-watersheds',
    label: 'Watersheds (USGS WBD)',
    category: 'other',
    geometryHint: 'polygon',
    kind: 'arcgis',
    refdataDataset: 'wbd-huc',
    nameField: 'NAME',
    descriptionFields: ['HUC', 'LEVEL'],
    defaultEnabled: false,
  },
  {
    id: 'nps-historic-places',
    label: 'Historic places (NRHP)',
    category: 'other',
    geometryHint: 'point',
    kind: 'arcgis',
    refdataDataset: 'nps-nrhp',
    nameField: 'RESNAME',
    descriptionFields: ['ResType', 'STATUS'],
    defaultEnabled: false,
  },
  {
    id: 'epa-frs-facilities',
    label: 'Regulated facilities (EPA FRS)',
    category: 'other',
    geometryHint: 'point',
    kind: 'arcgis',
    refdataDataset: 'epa-frs',
    nameField: 'NAME',
    descriptionFields: ['PROGRAMS'],
    defaultEnabled: false,
  },
] as const;

export function getCatalogEntry(id: string): ArcgisCatalogEntry | undefined {
  return ARCGIS_LAYER_CATALOG.find(e => e.id === id);
}

export function catalogEntriesByCategory(): Map<SiteLayerCategory, ArcgisCatalogEntry[]> {
  const map = new Map<SiteLayerCategory, ArcgisCatalogEntry[]>();
  for (const entry of ARCGIS_LAYER_CATALOG) {
    const list = map.get(entry.category) ?? [];
    list.push(entry);
    map.set(entry.category, list);
  }
  return map;
}
